<template>
  <tabbed-page class="customers-page">
    <div class="customers-page__header d-flex flex-column i-pt-11">
      <div class="d-flex justify-content-between align-items-center">
        <div class="customers-page__header-text">
          <router-link v-if="isFromAnalytics" to="/analytics">
            <i-icon-button
              data-testid="customers-page-back-button"
              symbol="medium-arrow-left"
              type="transparent"
            />
          </router-link>
          <div class="customers-page__header-title-subtitle">
            <div class="customers-page__header-title">Customers</div>
            <div class="customers-page__header-subtitle">
              View and manage the customers in your workspace
            </div>
          </div>
        </div>
        <div
          class="customers-page__header-buttons d-flex justify-space-between align-items-center i-gap-sm i-ml-6"
        >
          <i-button
            id="csvExport"
            data-testid="csv-export-button"
            text="Export"
            type="neutral"
            subtype="outline"
            :loading="exportLoading"
            @button-click="$emit('export-customers')"
          ></i-button>

          <b-tooltip target="csvExport" placement="right">
            Export CSV of customers matching current query
          </b-tooltip>

          <i-button
            text="Bulk Upload"
            data-testid="bulk-upload-button"
            type="neutral"
            subtype="outline"
            :disabled="isUploadAccessBlocked"
            @button-click="showBulkUpload = !showBulkUpload"
          ></i-button>

          <i-button
            text="New Customer"
            data-testid="new-customer-button"
            :disabled="isUploadAccessBlocked"
            @button-click="createNewCustomer()"
          ></i-button>
        </div>
      </div>

      <div
        v-if="showBulkUpload"
        class="d-flex flex-column customers-page__bulk-upload i-mt-11"
      >
        <div class="d-flex align-items-center justify-content-between">
          <div class="d-flex flex-column">
            <h5 class="i-text-button-1 i-text-neutral-120 i-mb-0">
              Bulk Uploads
            </h5>
            <p class="i-text-body-2 i-text-neutral-110">
              Upload a ZIP file containing all the files you want to analyze
            </p>
          </div>

          <i-icon-button
            symbol="medium-delete-1"
            size="md"
            type="transparent"
            @button-click="hideBulkUploadContainer()"
          ></i-icon-button>
        </div>

        <upload
          ref="upload"
          :auto-process-queue="true"
          :border="false"
          :max-files="1"
          :max-file-size="400"
          :api-url="`api/v2/documents/bulk`"
          :options="{
            addedfile: () => null,
            processing: fileAdded,
          }"
          :accepted-file-types="acceptedFileTypes"
          :include-styling="false"
          :class="{ 'd-none': hideUploadContainer }"
          class="relative w-100 upload-zone i-mt-9"
          @file-upload-progress="updateFileProgress"
          @upload-file-success="updateFileSuccess"
          @upload-error="uploadError"
        >
          <template slot="drag-overlay"> Drop a ZIP file here </template>
          <template slot="empty-state">
            <div
              class="empty-state d-flex flex-column w-100 justify-content-center align-items-center text-center i-py-12 i-px-8 i-bg-neutral-10"
            >
              <div
                class="d-flex align-items-center justify-content-center icon-wrapper i-mb-xl"
              >
                <i-icon symbol="medium-file-upload" :size="24" />
              </div>
              <h6 class="i-text-neutral-120 i-text-button-2 i-mb-1">
                Choose file or drag and drop
              </h6>
              <p class="i-text-neutral-100 i-text-body-2">
                Only ZIP files accepted &bull; Max 400mb
              </p>
            </div>
          </template>
        </upload>

        <i-alert-message
          v-if="showUploadCompleteMessage"
          class="i-mt-9"
          type="success"
          heading="Upload Complete!"
          message="Your documents are currently being organized by customer. Once processing is complete, we'll notify you via email."
        ></i-alert-message>

        <div
          v-if="bulkUploads.list.length > 0"
          class="i-mt-9 d-flex flex-column"
        >
          <div
            v-for="upload in bulkUploads.list"
            :key="upload.uploadId"
            class="d-flex justify-content-between align-items-center i-p-9 upload"
          >
            <div class="d-flex i-gap-lg">
              <div
                class="d-flex align-items-center justify-content-center icon-wrapper"
              >
                <i-icon symbol="medium-file-zip" :size="24" />
              </div>
              <div class="d-flex flex-column justify-content-center">
                <p class="i-text-button-2 i-text-neutral-120">
                  {{ upload.name }}
                </p>
                <div class="i-text-body-2 i-text-neutral-100">
                  <span
                    v-if="
                      upload.success &&
                      upload.state !== documentStates.PROCESSING
                    "
                    >Uploaded successfully</span
                  >
                  <span v-if="upload.error">{{ upload.error }} </span>
                  <span
                    v-if="
                      (upload.uploadProgress === 100 &&
                        !upload.success &&
                        !upload.error) ||
                      upload.state === documentStates.PROCESSING
                    "
                    >Processing...</span
                  >
                  <span v-else>
                    <span v-if="upload.size">
                      &bull;
                      <span v-if="upload.uploadProgress < 100">
                        {{
                          roundFileSize(
                            (upload.size * upload.uploadProgress) / 100,
                          )
                        }}
                        /
                      </span>
                      <span>{{ roundFileSize(upload.size) }}MB</span>
                    </span>
                    &bull;
                    <span v-if="upload.created_at">
                      {{ formatDateTime(upload.created_at, 'lll') }}</span
                    >
                  </span>
                </div>
              </div>
            </div>
            <div
              v-if="
                (upload.uploadProgress < 100 &&
                  (!upload.success || !upload.error)) ||
                (upload.uploadProgress === 100 &&
                  !upload.success &&
                  !upload.error) ||
                upload.state === documentStates.PROCESSING
              "
            >
              <i-spinner size="lg"></i-spinner>
            </div>
            <i-alert-icon
              v-if="
                upload.uploadProgress === 100 &&
                (upload.success || upload.error)
              "
              :type="upload.success ? 'success' : 'error'"
              size="xl"
              colour="dark"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="customers-page__search-filters">
      <CustomerSearchFilter />

      <CustomerStatusTabFilters class="i-mb-lg" />

      <customer-filters ref="customerFilters" />
    </div>

    <CreditInsightsIsAvailableBanner
      v-if="showCreditInsightsIsAvailableBanner"
      class="i-mb-6 i-mt-10"
    />

    <div class="i-mt-12">
      <customers-table />
    </div>

    <template v-if="hasPagination" slot="footer">
      <div class="i-pagination-wrapper i-py-10">
        <b-pagination
          id="customers-list-pagination"
          v-model="pageNumber"
          class="i-pagination"
          size="sm"
          :total-rows="customers.numberOfCustomers"
          :per-page="10"
          limit="5"
          aria-controls="customers-table"
          first-text="First"
          prev-text="Prev"
          next-text="Next"
          last-text="Last"
        >
          <template #first-text
            ><span class="chevron">
              <i-icon
                symbol="medium-chevron-left-double"
                :size="20"
              ></i-icon></span
          ></template>
          <template #prev-text>
            <span class="chevron">
              <i-icon symbol="medium-chevron-left" :size="20"></i-icon>
            </span>
          </template>
          <template #next-text>
            <span class="chevron">
              <i-icon symbol="medium-chevron-right" :size="20"></i-icon>
            </span>
          </template>
          <template #last-text>
            <span class="chevron">
              <i-icon symbol="medium-chevron-right-double" :size="20"></i-icon>
            </span>
          </template>
        </b-pagination>
      </div>
    </template>
    <router-view />
  </tabbed-page>
</template>

<script>
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex';
import { TabbedPage } from '@inscribe/ui-kit';
import {
  iIcon,
  iButton,
  iIconButton,
  iSpinner,
  iAlertMessage,
  iAlertIcon,
} from '@inscribe/signal';
import * as documentStates from '@/constants/documentStates';
import CustomersTable from './CustomersTable';
import Upload from '../documents/Upload';
import CustomerFilters from './CustomerFilters';
import CustomerStatusTabFilters from '@/V2/customers/CustomerStatusTabFilters';
import { BULK_UPLOAD } from '../../constants/onboardingChecklist';
import CreditInsightsIsAvailableBanner from './CreditInsightsIsAvailableBanner';
import CustomerSearchFilter from '@/V2/components/filters/CustomerSearchFilter';
import {
  deleteAllQueryParams,
  updateQueryParams,
  getAndSyncQueryParams,
} from '@/utils/queryParams';
import { formatDateTime } from '@/utils/date';

export default {
  components: {
    CustomerFilters,
    CustomerSearchFilter,
    CustomerStatusTabFilters,
    CustomersTable,
    TabbedPage,
    Upload,
    CreditInsightsIsAvailableBanner,
    iIcon,
    iButton,
    iIconButton,
    iSpinner,
    iAlertMessage,
    iAlertIcon,
  },
  props: {
    exportLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      pageNumber: this.$route.query.page || 1,
      acceptedFileTypes: [
        'application/zip',
        'application/octet-stream',
        'application/x-zip-compressed',
        'multipart/x-zip',
      ],
      hideUploadContainer: false,
      showBulkUpload: false,
      showUploadCompleteMessage: false,
      documentStates,
    };
  },
  computed: {
    ...mapGetters({
      canUseCredit: 'canUseCredit',
      creditInsightsIsAvailableBannerEnabled:
        'creditInsightsIsAvailableBannerEnabled',
      isUploadAccessBlocked: 'isUploadAccessBlocked',
    }),

    ...mapState(['customers', 'bulkUploads']),

    showCreditInsightsIsAvailableBanner() {
      return (
        this.creditInsightsIsAvailableBannerEnabled &&
        this.canUseCredit === false
      );
    },
    hasPagination() {
      return (
        this.customers.list.length > 0 &&
        !this.customers.customersLoading &&
        this.customers.numberOfCustomerPages > 1
      );
    },
    isFromAnalytics() {
      return this.$route.query.return_to === 'analytics';
    },
  },
  watch: {
    '$route.fullPath': {
      handler: function (newFullPath) {
        this.params = getAndSyncQueryParams({
          fullPath: newFullPath,
          router: this.$router,
          store: this.$store,
        });
      },
    },
    pageNumber() {
      updateQueryParams({
        fullPath: this.$route.fullPath,
        router: this.$router,
        store: this.$store,
        updates: {
          page: this.pageNumber,
        },
      });
    },
  },
  mounted() {
    this.getPendingBulkUploads().then((response) => {
      if (response.data.uploads) {
        const pendingUploads = response.data.uploads.map((upload) => ({
          ...upload,
          progress: 100,
          success: upload.state === documentStates.PROCESSING,
        }));
        this.setPendingBulkUploads(pendingUploads);
      }
    });
  },
  methods: {
    ...mapActions(['getPendingBulkUploads']),
    ...mapMutations([
      'setPendingBulkUploads',
      'addPendingBulkUpload',
      'updatePendingBulkUpload',
    ]),
    formatDateTime,

    fileAdded(file) {
      const fileUpload = {
        name: file.name,
        size: file.size / 1000000,
        uploadId: file.upload.uuid,
        uploadProgress: 0,
        created_at: Date.now(),
      };

      this.addPendingBulkUpload(fileUpload);
      this.hideUploadContainer = true;
    },

    roundFileSize(size, decimals = 2) {
      return Math.round(size * Math.pow(10, decimals)) / Math.pow(10, decimals);
    },

    updateFileProgress(file, uploadProgress) {
      this.updatePendingBulkUpload({
        uploadProgress,
        uploadId: file.upload.uuid,
      });
    },

    updateFileSuccess(file) {
      this.updatePendingBulkUpload({
        uploadId: file.upload.uuid,
        uploadProgress: 100,
        success: true,
      });

      updateQueryParams({
        fullPath: this.$route.fullPath,
        router: this.$router,
        store: this.$store,
        updates: {
          page: 1,
        },
      });

      this.$store.commit('addCompletedAction', BULK_UPLOAD);
      this.hideUploadContainer = true;
      this.showUploadCompleteMessage = true;
      this.$refs.upload.$refs.documentsDropzone.removeAllFiles();
    },

    uploadError(error, files) {
      this.$notify({
        group: 'general',
        type: 'error',
        title: 'Error',
        duration: 5000,
        text: error,
      });
      this.hideUploadContainer = true;

      files.forEach((file) => {
        this.updatePendingBulkUpload({
          uploadId: file.upload.uuid,
          error,
        });
      });
    },

    createNewCustomer() {
      deleteAllQueryParams({
        fullPath: this.$route.fullPath,
        router: this.$router,
        store: this.$store,
      });

      this.$router.push({
        name: 'new-customer',
      });
    },

    hideBulkUploadContainer() {
      this.showBulkUpload = false;
      if (this.showUploadCompleteMessage) {
        this.hideUploadContainer = false;
        this.showUploadCompleteMessage = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$max-width: 1200px;

.customers-page {
  &__header {
    .i-form-input-group .input-action:hover > .i-icon {
      color: $light-neutral-90 !important;
    }
  }

  &__header-text {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: map-get($increments, 6);
  }

  &__header-title-subtitle {
    display: flex;
    flex-direction: column;
  }

  &__header-title {
    font-size: map-get($text-size-heading, 6);
    font-weight: map-get($text-weights, 'semibold');
  }

  &__header-subtitle {
    font-size: map-get($text-size-standard, 2);
    color: $light-neutral-110;

    @include respond-to('small') {
      display: none;
    }
  }

  &__header-buttons {
    @include respond-to('large') {
      align-items: stretch !important;
    }
  }

  &__search-filters {
    position: sticky;
    top: 0;
    background-color: $light-neutral-default;
    z-index: 100;
  }

  &__bulk-upload {
    border: 1px solid $light-neutral-60 !important;
    background: $light-neutral-default;
    border-radius: map-get($increments, 10) !important;
    padding: map-get($increments, 10) !important;

    .empty-state {
      border: 1px dashed $light-neutral-60;
      border-radius: map-get($increments, 4);

      .icon-wrapper {
        background: $light-neutral-30;
        width: 88px;
        height: 88px;
        border-radius: 50%;
        .i-icon {
          color: $light-neutral-120 !important;
        }
      }
    }
    .uploaded-dropzone-item {
      display: none;
    }
    .upload {
      border: 1px solid $light-neutral-60;
      border-radius: map-get($increments, 8);

      &:not(:last-child) {
        margin-bottom: map-get($increments, 8);
      }

      .icon-wrapper {
        background: $light-primary-30;
        width: 64px;
        height: 64px;
        border-radius: 50%;
        .i-icon {
          color: $light-primary-110 !important;
        }
      }
    }
  }

  .i-pagination-wrapper {
    max-width: $max-width;
    margin: 0 auto;
    transform: translateX(-8px);
    .i-pagination {
      margin: 0;
    }
  }

  ::v-deep .tabbed-page-wrapper {
    .tabbed-page-content-wrapper {
      padding: 0 map-get($increments, 12) !important;
    }
    .tabbed-page-content {
      max-width: $max-width;
      padding: 0 !important;
    }
    .tabbed-page-footer {
      padding: 0 40px !important;
    }
  }
}
</style>

<style lang="scss">
.redesign-customers-page {
  background-color: $light-neutral-default !important;
}
</style>
